import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { FilePlus2, FolderOpen, Loader2, Cpu } from 'lucide-react';
import { usePW } from '@/lib/store';
import { modelsApi } from '@/lib/api';
import { GENRES } from '@/lib/data';
import { toast } from 'sonner';

export function NewProjectDialog({ open, onOpenChange }) {
  const { createProject } = usePW();
  const [chapters, setChapters] = useState([12]);
  const [wordsPer, setWordsPer] = useState([2500]);
  const [name, setName] = useState('Untitled Novel');
  const [genre, setGenre] = useState('romance');
  const [author, setAuthor] = useState('David Willis');
  const [folder, setFolder] = useState('');
  const [modelId, setModelId] = useState('claude-sonnet-4-5');
  const [models, setModels] = useState([]);
  const [pov, setPov] = useState('3rd_limited');
  const [tense, setTense] = useState('past');
  const [busy, setBusy] = useState(false);

  const POV_OPTIONS = [
    { value: '1st_person',  label: '1st Person' },
    { value: '2nd_person',  label: '2nd Person' },
    { value: '3rd_limited', label: '3rd Limited' },
    { value: '3rd_omni',    label: '3rd Omniscient' },
    { value: 'multiple',    label: 'Multiple POVs' },
  ];
  const TENSE_OPTIONS = [
    { value: 'past',    label: 'Past' },
    { value: 'present', label: 'Present' },
    { value: 'mixed',   label: 'Mixed' },
  ];

  useEffect(() => {
    let cancelled = false;
    modelsApi.list()
      .then((r) => {
        if (cancelled) return;
        setModels(r.models || []);
        if (r.default_id) setModelId(r.default_id);
      })
      .catch(() => { /* offline ok */ });
    return () => { cancelled = true; };
  }, []);

  const target = chapters[0] * wordsPer[0];

  const pickFolder = async () => {
    if (!window.showDirectoryPicker) {
      toast.error('Your browser doesn\'t support picking a local folder (try Chrome or Edge).');
      return;
    }
    try {
      const handle = await window.showDirectoryPicker();
      setFolder(handle.name);
    } catch (e) {
      // User cancelled the picker — not an error.
    }
  };

  const create = async () => {
    setBusy(true);
    try {
      await createProject({
        name,
        author,
        genre,
        chapters_count:    chapters[0],
        words_per_chapter: wordsPer[0],
        model_id:          modelId,
        pov,
        tense,
      });
      toast.success(`Project "${name}" created`);
      onOpenChange(false);
    } catch (e) {
      toast.error(`Could not create project: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 flex flex-col max-h-[92vh]">
        <DialogHeader className="shrink-0 border-b border-border bg-panel-header/40 px-5 py-3">
          <DialogTitle className="flex items-center gap-2 text-[14px]"><FilePlus2 className="h-4 w-4 text-primary" /> New Project</DialogTitle>
          <DialogDescription className="sr-only">Create a new novel project. Choose chapter count, words per chapter, genre, and author.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 px-5 py-5 md:grid-cols-2 overflow-y-auto flex-1 min-h-0">
          <div className="space-y-4">
            <Field label="Project name">
              <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-[12.5px]" />
            </Field>
            <Field label="Author">
              <Input value={author} onChange={(e) => setAuthor(e.target.value)} className="h-8 text-[12.5px]" />
            </Field>
            <Field label="Genre">
              <Select value={genre} onValueChange={setGenre}>
                <SelectTrigger className="h-8 text-[12.5px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GENRES.map((g) => <SelectItem key={g.id} value={g.id}>{g.label} <span className="text-muted-foreground">— {g.sub}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Folder on PC">
              <div className="flex gap-1.5">
                <Input value={folder} onChange={(e) => setFolder(e.target.value)}
                  placeholder="Pick a folder, or type a name for your reference"
                  className="h-8 font-mono-stat text-[11.5px]" data-testid="new-project-folder-input" />
                <Button variant="outline" size="sm" className="h-8 gap-1.5 text-[12px]"
                  onClick={pickFolder} data-testid="new-project-folder-open-btn" title="Open folder picker">
                  <FolderOpen className="h-3.5 w-3.5" />
                </Button>
              </div>
            </Field>
            <Field label={`Chapter count · ${chapters[0]} chapters`}>
              <Slider value={chapters} onValueChange={setChapters} min={5} max={60} step={1} />
              <div className="mt-1 flex justify-between text-[10.5px] text-muted-foreground"><span>5</span><span>20</span><span>40</span><span>60</span></div>
            </Field>
            <Field label={`Words per chapter · ~${wordsPer[0].toLocaleString()} words`}>
              <Slider value={wordsPer} onValueChange={setWordsPer} min={1500} max={5000} step={100} />
              <div className="mt-1 flex justify-between text-[10.5px] text-muted-foreground"><span>1.5k</span><span>2.5k</span><span>5k</span></div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Point of View">
                <Select value={pov} onValueChange={setPov}>
                  <SelectTrigger className="h-8 text-[12.5px]" data-testid="new-project-pov-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {POV_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Tense">
                <Select value={tense} onValueChange={setTense}>
                  <SelectTrigger className="h-8 text-[12.5px]" data-testid="new-project-tense-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TENSE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="AI model">
              <Select value={modelId} onValueChange={setModelId}>
                <SelectTrigger className="h-8 text-[12.5px]" data-testid="new-project-model-trigger">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  {models.length === 0 && <SelectItem value="claude-sonnet-4-5">Claude Sonnet 4.5</SelectItem>}
                  {models.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`new-project-model-option-${m.id}`}>
                      <span className="font-medium">{m.label}</span>
                      <span className="ml-1.5 text-muted-foreground">— {m.family.replace(/^[^ ]+ /, '')}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="rounded-md border border-border bg-panel-header/30 p-4">
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Project blueprint</div>
            <h3 className="mt-1 font-serif-novel text-2xl leading-tight">{name || 'Untitled Novel'}</h3>
            <p className="mt-1 text-[11.5px] text-muted-foreground">{GENRES.find((g) => g.id === genre)?.label} — {chapters[0]} chapters, ~{wordsPer[0].toLocaleString()} words each.</p>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11.5px]">
              <Mini k="Chapters" v={String(chapters[0])} />
              <Mini k="Target" v={`${target.toLocaleString()} words`} />
              <Mini k="POV" v={POV_OPTIONS.find((o) => o.value === pov)?.label || '3rd Limited'} />
              <Mini k="Tense" v={TENSE_OPTIONS.find((o) => o.value === tense)?.label || 'Past'} />
              <Mini k="AI Model" v={models.find((m) => m.id === modelId)?.label || 'Claude Sonnet 4.5'} />
            </div>
            <p className="mt-3 text-[10.5px] text-muted-foreground">Variables, characters, and modes are initialized from the Drafting preset. You can adjust everything afterwards.</p>
          </div>
        </div>
        <DialogFooter className="shrink-0 border-t border-border bg-panel-header/40 px-5 py-3">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={create} disabled={busy} data-testid="new-project-create-btn">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Create Project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) { return <div><Label className="text-[11.5px]">{label}</Label><div className="mt-1">{children}</div></div>; }
function Mini({ k, v }) { return <div className="rounded border border-border bg-card px-2 py-1.5"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div><div className="font-mono-stat text-[12px] font-medium">{v}</div></div>; }
